import type { ComponentProps } from "react";
import classnames from "classnames";

import Button, {
  TooltipButtonContainer,
  type ButtonColors,
  type ButtonSizes,
  type ButtonVariants,
  type AriaPopoutProps,
} from "common/core/button";
import TooltipOverlay from "common/core/tooltip/overlay";
import Icon from "common/core/icon";

import Styles from "./index.module.scss";

type OverlayProps = ComponentProps<typeof TooltipOverlay>;

type IconButtonProps = AriaPopoutProps & {
  className?: string;
  isLoading?: boolean;
  onClick: () => void;
  disabled?: boolean;
  automationId?: string;
  name: string;
  "aria-pressed"?: boolean;
  iconClassName?: string;
  /** Determines the style of the button */
  variant?: ButtonVariants;
  /** Determines the color of the button */
  buttonColor?: ButtonColors;
  /** Determines the size of the button */
  buttonSize?: ButtonSizes;
  /** Use either hoverLabel or aria-label, not both together */
  hoverLabel?: string;
  "aria-label"?: string;
  hoverLabelPlacement?: OverlayProps["placement"];
};

/**
 * This component is basically a place for the styles related to icons embedded within buttons and establishing
 * a clear interface for devs that want to use circular buttons with icons in them
 */
function IconButton({
  className,
  iconClassName,
  isLoading,
  onClick,
  disabled,
  automationId,
  name,
  buttonColor,
  buttonSize,
  variant,
  "aria-label": ariaLabel,
  "aria-pressed": ariaPressed,
  hoverLabel,
  hoverLabelPlacement = "top",
  ...props
}: IconButtonProps) {
  const buttonClassName = classnames(
    className,
    Styles.iconButton,
    buttonSize && Styles[buttonSize],
  );
  return (
    <TooltipButtonContainer hoverLabel={hoverLabel} fullWidth={false}>
      <>
        <Button
          className={buttonClassName}
          isLoading={isLoading}
          onClick={onClick}
          disabled={disabled}
          automationId={automationId}
          aria-pressed={ariaPressed}
          aria-label={hoverLabel || ariaLabel}
          variant={variant}
          buttonColor={buttonColor}
          buttonSize={buttonSize}
          {...props}
        >
          <Icon name={name} className={iconClassName} />
        </Button>
        {hoverLabel && (
          <TooltipOverlay
            aria-hidden="true"
            trigger="hover"
            placement={hoverLabelPlacement}
            size="mini"
          >
            {hoverLabel}
          </TooltipOverlay>
        )}
      </>
    </TooltipButtonContainer>
  );
}

export default IconButton;
